import { useOrgOrderItem } from '../../features/org-orders/api';
import { ManageItemDialog } from './OrgOrderRow';

/**
 * Gives the manage-load dialog a data source of its own.
 *
 * The dialog used to be handed a row object lifted straight out of the orders
 * table's list query, which tied the lifetime of the thing being edited to the
 * table's current filter and page. A save that changed status moved the load out
 * of the filtered set, the row vanished from the next refetch, and the dialog
 * was left holding data nothing would ever refresh again — which is how a load
 * that had saved cleanly kept insisting it had unsaved changes.
 *
 * Reading by id removes the coupling. The list row is still passed through as a
 * placeholder so the dialog opens instantly on data the table already has, then
 * swaps to the authoritative copy. Both come from the same server-side builder,
 * so the two cannot disagree about shape.
 *
 * The props below are forwarded untouched — this wrapper exists to change where
 * `item` comes from and nothing else.
 */
interface ManageLoadDialogProps {
  itemId: string;
  /** The list row the dialog was opened from, shown until the fetch lands. */
  placeholderItem?: unknown;
  onClose: () => void;
  onSave: (payload: any, isApprovedAlready: boolean) => Promise<boolean | void>;
  onReject: (payload: { orderId: string; itemId: string; reason?: string }) => Promise<void>;
  isSaving?: boolean;
  isRejecting?: boolean;
  onReplicatedItem?: (item: any) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  currentPosition?: number;
  totalItems?: number;
  isNavigating?: boolean;
}

export function ManageLoadDialog({ itemId, placeholderItem, ...rest }: ManageLoadDialogProps) {
  /*
   * A caller that only has an id — the audit modal's "Recent loads" tab — passes
   * `{ id }`. That is not a row, and rendering the form against it throws, so it
   * counts as no placeholder and the dialog waits for the fetch.
   */
  const placeholder = (placeholderItem as any)?.order ? placeholderItem : undefined;

  const { data: item, isError, error } = useOrgOrderItem(itemId, placeholder);

  /*
   * With a placeholder there is always something to render on the first frame,
   * so there is no loading state to show. Without one — reopening from a
   * notification, say — `item` is undefined until the request resolves.
   */
  if (!item) {
    if (isError) {
      return (
        <div className="modal-backdrop" role="alert">
          <div className="modal">
            <p>
              {(error as any)?.response?.data?.error ?? 'This load could not be loaded. Try again.'}
            </p>
            <button className="btn" onClick={rest.onClose}>
              Close
            </button>
          </div>
        </div>
      );
    }

    return null;
  }

  /*
   * Keyed by load, so stepping through with prev/next remounts the dialog and
   * builds its form from the load actually on screen.
   *
   * Without this the form was built once and kept: `useModal.open()` only swaps
   * the data it holds, so navigating from one load to the next left the previous
   * load's edits sitting in the fields. The key does not fire when the
   * placeholder swaps to the fetched copy — same id, same load — so nothing a
   * user has typed is thrown away when the request lands.
   */
  return <ManageItemDialog key={itemId} item={item} {...rest} />;
}
